import { motion } from "framer-motion"; // Using standard framer-motion import
import { useEffect, useState } from "react";
import {
  Gauge,
  Building2,
  Droplets,
  CircleDot,
  Activity,
  Waves,
  Zap,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Users,
  Hash,
  CheckCircle,
  XCircle,
  Map,
} from "lucide-react";
import Meters from "../components/Meters";
import Stats from "../components/Stats";
import DigitalTwin from "../components/DigitalTwin";
import { getTwinStats, getTwinMeter } from "../services";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import MapTab from "../components/MapTab";
const OPERATIONAL_ORDER = {
  orange: 1,
  green: 2,
  red: 3,
};

export function Schematics({ onBack }) {
  const selectedDistrict = useDistrict();
  const [activeTab, setActiveTab] = useState("digitalTwin");
  const [hoveredNode, setHoveredNode] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [statsApi, setStatsApi] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statusToggle, setStatusToggle] = useState(true);
  const { theme, colors } = useTheme();

  // Ftech Meter
  const [metersData, setMetersData] = useState([]);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const [activeFilter, setActiveFilter] = useState([]);
  // ["active", "inactive"]

  const [operationalFilter, setOperationalFilter] = useState([]);
  // ["green", "orange", "red"]

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  // const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (activeTab === "stats") {
      fetchStats();
    }
  }, [activeTab, selectedDistrict]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await getTwinStats();
      setStatsApi(res?.data);
    } catch (e) {
      console.error("Stats API failed", e);
    } finally {
      setStatsLoading(false);
    }
  };

  const isSearchMode = Boolean(
    searchTerm ||
    activeFilter.length ||
    operationalFilter.length ||
    month ||
    year,
  );

  // Fetch meters data
  const fetchMeters = async (page = 1, limit = itemsPerPage) => {
    setLoadingMeters(true);
    try {
      const res = await getTwinMeter({
        page: isSearchMode ? 1 : page,
        page_limit: isSearchMode ? 10000 : limit,
        search: searchTerm,
        active: statusToggle ? "active" : "all",
        active_status: activeFilter,
        operational_status: operationalFilter,
        month,
        year,
      });

      const data = res?.data || {};

      setMetersData(
        (data.meters || []).map((m) => ({
          active: m.endpoint_status === "Active",
          operational: m.operational || "green",
          account: m.overided_name || "-",
          meterId: m.meter_id || "-",
          register: m.register_number || "-",
          dateRange: m.date_range || "-",
          meter_sn: m.meter_sn || "-",
          meterType: m.meter_type || "-",
          model: m.meter_model || "-",
          size: m.meter_size || "-",
          dials: m.dials || "-",
        })),
      );

      setTotalItems(data.total_records || 0);
    } catch (err) {
      console.error("Meter fetch failed", err);
    } finally {
      setLoadingMeters(false);
    }
  };

  useEffect(() => {
    if (activeTab === "meters") {
      fetchMeters(currentPage, itemsPerPage);
    }
  }, [
    activeTab,
    selectedDistrict,
    currentPage,
    itemsPerPage,
    searchTerm,
    activeFilter,
    operationalFilter,
    statusToggle,
    month,
    year,
  ]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = metersData.filter((meter) => {
    // 🔍 Search filter
    const matchesSearch =
      !searchTerm ||
      Object.values(meter)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    // ✅ Active filter
    const matchesActive =
      activeFilter.length === 0 ||
      activeFilter.includes(meter.active ? "active" : "inactive");

    // 🎯 Operational filter
    const matchesOperational =
      operationalFilter.length === 0 ||
      operationalFilter.includes(meter.operational);

    // 📅 Month / Year filter
    let matchesDate = true;

    if (month || year) {
      const date = new Date(meter.dateRange);
      if (!isNaN(date)) {
        if (month && date.getMonth() + 1 !== Number(month)) {
          matchesDate = false;
        }
        if (year && date.getFullYear() !== Number(year)) {
          matchesDate = false;
        }
      }
    }

    return matchesSearch && matchesActive && matchesOperational && matchesDate;
  });

  const handleSort = (column) => {
    let direction = sortDirection;
    if (sortColumn === column) {
      direction = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(direction);
    } else {
      setSortColumn(column);
      direction = "asc";
      setSortDirection("asc");
    }

    setMetersData((prev) => {
      const sorted = [...prev].sort((a, b) => {
        if (column === "operational") {
          const aVal = OPERATIONAL_ORDER[a.operational] || 99;
          const bVal = OPERATIONAL_ORDER[b.operational] || 99;
          return direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aVal = a[column] ?? "";
        const bVal = b[column] ?? "";

        return direction === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });

      return sorted;
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter, operationalFilter, month, year]);

  // end meter fetch
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nodes = [
    {
      id: "hydrant1",
      type: "hydrant",
      label: "Hydrant",
      x: 20,
      y: 10,
      status: "warning",
    },
    { id: "valve1", type: "valve", label: "Valve", x: 35, y: 10 },
    {
      id: "hydrant2",
      type: "hydrant",
      label: "Hydrant",
      x: 50,
      y: 10,
      status: "warning",
    },
    {
      id: "building1",
      type: "building",
      label: "Non-Potable Building",
      x: 65,
      y: 10,
    },
    { id: "pump1", type: "pump", label: "AR Pump", x: 20, y: 25 },
    { id: "meter1", type: "meter", label: "Meter Group", x: 38, y: 28 },
    { id: "tank1", type: "tank", label: "Water Meter", x: 65, y: 25 },
    {
      id: "hydrant3",
      type: "hydrant",
      label: "Hydrant 203-3",
      x: 20,
      y: 40,
      status: "warning",
    },
    {
      id: "regulator1",
      type: "regulator",
      label: "Pressure Regulator",
      x: 35,
      y: 40,
    },
    {
      id: "hydrant4",
      type: "hydrant",
      label: "Hydrant 203-2",
      x: 50,
      y: 40,
      status: "warning",
    },
    {
      id: "hydrant5",
      type: "hydrant",
      label: "Hydrant 203-2.1",
      x: 58,
      y: 40,
      status: "warning",
    },
    {
      id: "hydrant6",
      type: "hydrant",
      label: "Hydrant 130",
      x: 75,
      y: 40,
      status: "warning",
    },
    {
      id: "hydrant7",
      type: "hydrant",
      label: "Hydrant",
      x: 12,
      y: 58,
      status: "warning",
    },
    {
      id: "hydrant8",
      type: "hydrant",
      label: "Hydrant",
      x: 23,
      y: 58,
      status: "warning",
    },
    { id: "valve2", type: "valve", label: "Potable Valve", x: 35, y: 58 },
    {
      id: "hydrant9",
      type: "hydrant",
      label: "Curbed Hydrant",
      x: 47,
      y: 58,
      status: "warning",
    },
    {
      id: "hydrant10",
      type: "hydrant",
      label: "Hydrant",
      x: 58,
      y: 58,
      status: "warning",
    },
    { id: "valve3", type: "valve", label: "Isolation Valve", x: 68, y: 58 },
    {
      id: "hydrant11",
      type: "hydrant",
      label: "Hydrant",
      x: 78,
      y: 58,
      status: "warning",
    },
    { id: "valve4", type: "valve", label: "Potable Valve", x: 35, y: 75 },
    {
      id: "valve5",
      type: "valve",
      label: "Non-Potable Building",
      x: 68,
      y: 75,
    },
  ];

  const connections = [
    { from: "hydrant1", to: "valve1" },
    { from: "valve1", to: "hydrant2" },
    { from: "valve1", to: "meter1", type: "dashed" },
    { from: "building1", to: "tank1" },
    { from: "pump1", to: "hydrant3" },
    { from: "meter1", to: "regulator1" },
    { from: "tank1", to: "hydrant6" },
    { from: "hydrant3", to: "hydrant7" },
    { from: "hydrant3", to: "hydrant8" },
    { from: "regulator1", to: "valve2" },
    { from: "hydrant4", to: "hydrant9" },
    { from: "hydrant5", to: "hydrant10", type: "dashed" },
    { from: "hydrant6", to: "valve3" },
    { from: "valve2", to: "valve4" },
    { from: "valve3", to: "valve5" },
    { from: "valve3", to: "hydrant11" },
  ];

  const getNodeIcon = (type, status) => {
    const iconClass = status === "warning" ? "text-red-400" : "text-[#00b8f1]";
    const iconSize = "w-6 h-6";

    switch (type) {
      case "hydrant":
        return <CircleDot className={`${iconSize} ${iconClass}`} />;
      case "valve":
        return <Gauge className={`${iconSize} ${iconClass}`} />;
      case "meter":
        return <Activity className={`${iconSize} ${iconClass}`} />;
      case "pump":
        return <Zap className={`${iconSize} ${iconClass}`} />;
      case "tank":
        return <Droplets className={`${iconSize} ${iconClass}`} />;
      case "regulator":
        return <Gauge className={`${iconSize} ${iconClass}`} />;
      case "building":
        return <Building2 className={`${iconSize} ${iconClass}`} />;
      default:
        return <CircleDot className={`${iconSize} ${iconClass}`} />;
    }
  };

  return (
    <>
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1
          className="text-4xl font-bold bg-clip-text text-transparent"
          style={{
            backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: "normal",
          }}
        >
          Digital Twin
        </h1>
        <p
          className="text-[#BDB9B5] text-sm mt-1"
          style={{ color: colors.textSecondary }}
        >
          Interactive water distribution network visualization
        </p>
      </motion.div>

      <motion.div
        className="flex gap-3 mb-6 flex-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <button
          onClick={() => setActiveTab("digitalTwin")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "digitalTwin"
                ? `${colors.primary}20`
                : colors.cardBg,
            borderColor:
              activeTab === "digitalTwin" ? colors.primary : colors.border,
          }}
        >
          {activeTab === "digitalTwin" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <Waves
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "digitalTwin"
                  ? colors.primary
                  : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color:
                activeTab === "digitalTwin"
                  ? colors.text
                  : colors.textSecondary,
            }}
          >
            Digital Twin
          </span>
        </button>

        <button
          onClick={() => setActiveTab("stats")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "stats" ? `${colors.primary}20` : colors.cardBg,
            borderColor: activeTab === "stats" ? colors.primary : colors.border,
          }}
        >
          {activeTab === "stats" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <Activity
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "stats" ? colors.primary : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color: activeTab === "stats" ? colors.text : colors.textSecondary,
            }}
          >
            Stats
          </span>
        </button>

        <button
          onClick={() => setActiveTab("meters")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "meters" ? `${colors.primary}20` : colors.cardBg,
            borderColor:
              activeTab === "meters" ? colors.primary : colors.border,
          }}
        >
          {activeTab === "meters" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <Gauge
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "meters" ? colors.primary : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color:
                activeTab === "meters" ? colors.text : colors.textSecondary,
            }}
          >
            Meters
          </span>
        </button>
        <button
          onClick={() => setActiveTab("map")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "map" ? `${colors.primary}20` : colors.cardBg,
            borderColor: activeTab === "map" ? colors.primary : colors.border,
          }}
        >
          <Map
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "map" ? colors.primary : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color: activeTab === "map" ? colors.text : colors.textSecondary,
            }}
          >
            Map View
          </span>
        </button>
      </motion.div>

      {activeTab === "digitalTwin" && (
        <DigitalTwin
          nodes={nodes}
          connections={connections}
          hoveredNode={hoveredNode}
          setHoveredNode={setHoveredNode}
          getNodeIcon={getNodeIcon}
        />
      )}
     
      {activeTab === "map" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-xl border p-4 h-[90vh] mb-5"
          style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
        >
          <MapTab theme={theme} colors={colors} />
        </motion.div>
      )}
      {activeTab === "stats" && (
        <Stats
          loading={statsLoading}
          summary={[
            {
              label: "Accounts",
              value:
                statsApi?.count?.find((i) => i.name === "Accounts")?.count || 0,
              icon: "users",
            },
            // {
            //   label: "Meters",
            //   value:
            //     statsApi?.count?.find((i) => i.name === "Meters")?.count || 0,
            //   icon: "gauge",
            // },
            {
              label: "Meters",
              value:
                statsApi?.count?.find((i) => i.name === "Unique Meter IDs")
                  ?.count || 0,
              icon: "hash",
            },
            {
              label: "Operational",
              value:
                statsApi?.count?.find((i) => i.name === "Operational Meters")
                  ?.count || 0,
              icon: "check",
              // color: "text-green-400",
            },
            {
              label: "Non-Operational",
              value:
                statsApi?.count?.find(
                  (i) => i.name === "Non-Operational Meters",
                )?.count || 0,
              icon: "x",
              // color: "text-red-400",
            },
          ]}
          categoryStats={
            statsApi
              ? statsApi.operational_category_count.map((op) => {
                  const nonOp = statsApi.non_operational_category_count.find(
                    (n) => n.name === op.name,
                  );
                  return [op.name, op.count, nonOp?.count || 0];
                })
              : []
          }
        />
      )}

      {activeTab === "meters" && (
        <Meters
          currentData={currentData}
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          pageLimit={itemsPerPage}
          setPageLimit={setItemsPerPage}
          handlePageChange={handlePageChange}
          handleSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusToggle={statusToggle}
          setStatusToggle={setStatusToggle}
          filterOpen={filterOpen}
          setFilterOpen={setFilterOpen}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          operationalFilter={operationalFilter}
          setOperationalFilter={setOperationalFilter}
          month={month}
          setMonth={setMonth}
          year={year}
          setYear={setYear}
          isSearchMode={isSearchMode}
          loading={loadingMeters}
        />
      )}
    </>
  );
}
export default Schematics;
